'use client';

import React from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Calendar, 
  Users, 
  Tag, 
  Coins, 
  Bell 
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface SalesHeaderProps {
  salesCount: number;
  currentDateStr: string;
  selectedRange: 'day' | 'week' | 'month' | 'all';
  onRangeChange: (range: 'day' | 'week' | 'month' | 'all') => void;
  onPrevDate: () => void;
  onNextDate: () => void;
  onToday: () => void;
  isToday: boolean;
  dateLabel: string;
  onOpenChargeModal: () => void;
}

export default function SalesHeader({
  salesCount,
  selectedRange,
  onRangeChange,
  onPrevDate,
  onNextDate,
  onToday,
  isToday,
  dateLabel,
  onOpenChargeModal
}: SalesHeaderProps) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2">
      {/* Lado izquierdo: Título, Navegación de Fecha y Pastillas de Período */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        {/* Título y Conteo */}
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight">
            Caja y Ventas
          </h1>
          <span className="text-slate-400 font-normal text-xl">·</span>
          <span className="text-slate-500 font-medium text-lg">
            {salesCount} transacciones
          </span>
        </div>

        {/* Navegador de Fecha < Hoy > */}
        <div className="flex items-center bg-white border border-slate-200/90 rounded-xl px-2 py-1 shadow-xs">
          <button
            onClick={onPrevDate}
            className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition cursor-pointer"
            title="Día anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={onToday}
            className="px-3 py-0.5 text-xs font-bold text-slate-800 hover:text-[#6255F5] transition min-w-[70px] text-center cursor-pointer"
          >
            {isToday ? 'Hoy' : dateLabel}
          </button>
          <button
            onClick={onNextDate}
            className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition cursor-pointer"
            title="Día siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Pastillas de Período [ Día | Semana | Mes | Todo ] */}
        <div className="flex items-center bg-white border border-slate-200/90 p-1 rounded-xl shadow-xs text-xs font-semibold">
          <button
            onClick={() => onRangeChange('day')}
            className={`px-3 py-1 rounded-lg transition cursor-pointer ${
              selectedRange === 'day'
                ? 'bg-[#6255F5] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Día
          </button>
          <button
            onClick={() => onRangeChange('week')}
            className={`px-3 py-1 rounded-lg transition cursor-pointer ${
              selectedRange === 'week'
                ? 'bg-[#6255F5] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => onRangeChange('month')}
            className={`px-3 py-1 rounded-lg transition cursor-pointer ${
              selectedRange === 'month'
                ? 'bg-[#6255F5] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => onRangeChange('all')}
            className={`px-3 py-1 rounded-lg transition cursor-pointer ${
              selectedRange === 'all'
                ? 'bg-[#6255F5] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Todo
          </button>
        </div>
      </div>

      {/* Lado derecho: Botón Principal de Cobro en Mostrador */}
      <div className="flex items-center gap-2 self-end lg:self-center">
        <Button
          variant="primary"
          onClick={onOpenChargeModal}
          className="shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Cobrar en Mostrador</span>
        </Button>
      </div>
    </div>
  );
}
